import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import _ from 'lodash';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import { convertArraysToSets } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';
import { BulkUploadInfo, EditedRecordOptions } from 'types/pricingSimulator';

interface FetchScenarioDetailsProp extends HookInputProps {}

function useFetchScenarioDetails({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
}: FetchScenarioDetailsProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);

    const {
        updateScenarioProperty,
        resetFilterObject,
        setAllCheckedAttribute,
        setAllSelectedValueForAllAttribute,
        setShowIsEditedRecords,
        setApplyFilterObject,
    } = useContext(pricingSimulatorDataContext);

    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const dim1DropdownList = dimensionConfigData?.dim1DropdownList ?? [];
    const dim2DropdownList = dimensionConfigData?.dim2DropdownList ?? [];
    const defaultDim1DropdownItem = dimensionConfigData?.defaultDim1DropdownItem ?? {
        value: '',
        label: '',
        dimension: '',
    };
    const defaultDim2DropdownItem = dimensionConfigData?.defaultDim2DropdownItem ?? {
        value: '',
        label: '',
        dimension: '',
    };
    const { appConfig } = useContext(UserContext);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);
            updateScenarioProperty('scenarioDetailsLoading', true);

            try {
                const data = await axios({
                    method,
                    url,
                    params: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            // 1. setting filters
            if (payload?.filters === '{}' || Object.keys(payload?.filters).length === 0) {
                resetFilterObject();
            } else {
                const parsedData = convertArraysToSets(payload.filters);
                setAllCheckedAttribute(parsedData?.allCheckedAttribute);
                setAllSelectedValueForAllAttribute(parsedData?.allSelectedValueForAllAttribute);
                // todo: remove the below conditions once migration is done for all the customers. For time being fall back to support existing records
                setShowIsEditedRecords(
                    ['true', 'yes'].includes(parsedData?.showIsEditedRecords)
                        ? EditedRecordOptions.YES
                        : ['false', 'all'].includes(parsedData?.showIsEditedRecords)
                        ? EditedRecordOptions.ALL
                        : EditedRecordOptions.NO,
                );
                setApplyFilterObject({ ...parsedData?.applyFilterObject });
            }

            // 2. setting dim selection information
            const allDims = [...dim1DropdownList, ...dim2DropdownList];

            let selectedAttributes = [defaultDim1DropdownItem, defaultDim2DropdownItem];
            if (payload?.dimension_attributes?.length > 0) {
                selectedAttributes = payload.dimension_attributes.map((key: string) =>
                    allDims.find((d) => d.value === key),
                );
            }

            updateScenarioProperty('dimAttributes', selectedAttributes);

            let sortObject: any = {
                sortBy: defaultDim1DropdownItem?.value,
                sortingOrder: 'asc',
                sortType: 'actual',
            };
            // 3. updating sort object
            if (payload?.sort_obj) {
                sortObject = {
                    sortBy: payload?.sort_obj.sort_by ?? defaultDim1DropdownItem?.value,
                    sortingOrder: payload?.sort_obj?.sorting_order ?? 'asc',
                    sortType: payload?.sort_obj?.sort_type ?? 'actual',
                };
            }
            updateScenarioProperty('sortObject', sortObject);

            // 4. upload status
            const uploadStatus = payload?.upload_status || '';
            const uploadError = payload?.upload_error_message || '';
            const bulkUploadInfo: BulkUploadInfo = { errorObject: uploadError, status: uploadStatus };
            updateScenarioProperty('bulkUploadInfo', bulkUploadInfo);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
            updateScenarioProperty('scenarioDetailsLoading', false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}

export { useFetchScenarioDetails };
